// src/components/layout/MainLayout.tsx

import React from 'react';
import { Menu } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { useSidebar } from '@/context/SidebarContext';
import { useIsMobile } from '@/hooks/useIsMobile';

interface MainLayoutProps {
  children: React.ReactNode;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { collapsed, toggleMobile } = useSidebar();
  const isMobile = useIsMobile();

  const marginLeft = isMobile ? 0 : (collapsed ? 68 : 256);

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 relative overflow-hidden">
      <div
        className="fixed inset-0 pointer-events-none overflow-hidden transition-all duration-300"
        style={{ marginLeft }}
      >
        <div className="absolute inset-0 opacity-20">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, rgba(59, 130, 246, 0.1) 1px, transparent 1px),
                 linear-gradient(to bottom, rgba(59, 130, 246, 0.1) 1px, transparent 1px)`,
              backgroundSize: '80px 80px',
            }}
          />
        </div>

        <div className="absolute top-10 left-10 w-96 h-96 rounded-full blur-3xl animate-float bg-blue-500/20" />
        <div className="absolute top-1/4 right-20 w-80 h-80 rounded-full blur-3xl animate-float-delayed bg-purple-500/15" />
        <div className="absolute bottom-20 left-1/4 w-72 h-72 rounded-full blur-3xl animate-float-slow bg-cyan-500/20" />
        <div className="absolute bottom-1/3 right-1/3 w-64 h-64 rounded-full blur-3xl animate-pulse bg-indigo-500/15" />

        <svg className="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="gear-pattern" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse">
              <circle cx="100" cy="100" r="30" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-400" />
              <circle cx="100" cy="100" r="15" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-400" />
              <rect x="95" y="70" width="10" height="15" fill="currentColor" className="text-blue-400/50" />
              <rect x="95" y="115" width="10" height="15" fill="currentColor" className="text-blue-400/50" />
              <rect x="70" y="95" width="15" height="10" fill="currentColor" className="text-blue-400/50" />
              <rect x="115" y="95" width="15" height="10" fill="currentColor" className="text-blue-400/50" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#gear-pattern)" />
        </svg>

        <div className="absolute top-0 right-0 w-1/2 h-full opacity-15">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="hexagons" width="100" height="87" patternUnits="userSpaceOnUse">
                <polygon points="50,0 93,25 93,62 50,87 7,62 7,25" fill="none" stroke="rgb(59, 130, 246)" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hexagons)" />
          </svg>
        </div>

        <svg className="absolute inset-0 w-full h-full opacity-15" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="line-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgb(59, 130, 246)" stopOpacity="0.5" />
              <stop offset="50%" stopColor="rgb(147, 51, 234)" stopOpacity="0.5" />
              <stop offset="100%" stopColor="rgb(59, 130, 246)" stopOpacity="0.5" />
            </linearGradient>
          </defs>
          <path d="M 0 200 Q 200 100 400 200 T 800 200" stroke="url(#line-gradient)" strokeWidth="2" fill="none" className="animate-flow-line" />
          <path d="M 0 400 Q 300 300 600 400 T 1200 400" stroke="url(#line-gradient)" strokeWidth="2" fill="none" className="animate-flow-line-delayed" />
        </svg>

        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-[0.05]">
          <svg width="400" height="400" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="1" className="text-blue-400" />
            <circle cx="50" cy="50" r="35" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-blue-400" />
            <text x="50" y="45" textAnchor="middle" fontSize="12" fill="currentColor" className="text-blue-400 font-bold">IATF</text>
            <text x="50" y="58" textAnchor="middle" fontSize="10" fill="currentColor" className="text-blue-400">16949</text>
            <text x="50" y="68" textAnchor="middle" fontSize="6" fill="currentColor" className="text-blue-400">:2016</text>
          </svg>
        </div>
      </div>

      <Sidebar />

      <button
        onClick={toggleMobile}
        className="fixed top-3 left-3 z-30 p-2.5 rounded-xl shadow-lg md:hidden backdrop-blur-sm transition-colors bg-slate-800/90 border border-white/10 text-white"
        aria-label="Abrir menú"
      >
        <Menu className="h-5 w-5" />
      </button>

      <main
        className="flex-1 transition-all duration-300 overflow-auto min-w-0 flex flex-col"
        style={{ marginLeft }}
      >
        <TopBar />
        <div className="p-4 pt-4 md:p-8 md:pt-6 flex flex-col flex-1">
          {children}
        </div>
      </main>
    </div>
  );
};
